import { useTranslation } from 'react-i18next'

import { useAppSelector } from '@/hooks/store'
import { Service } from '@/models/service'
import { cartSlice } from '@/store/cartSlice'
import { selectTotalGlobalSelectedQuantity } from '@/store/cartSlice/selectors/selectTotalGlobalSelectedQuantity.ts'
import { totalGlobalBoughtQuantitySelector } from '@/store/cartSlice/selectors/totalGlobalBoughtQuantitySelector'
import {
  mapSelectedElementHasVisibleSeatsSelector,
  mapSelectedElementServices,
  mapSelectors,
} from '@/store/mapSlice'
import { getGroupBoughtQuantity } from '@/utils/getGroupBoughtQuantity'
import { getVisibleServices } from '@/utils/getVisibleServices'
import { sumServiceGroupSelectedQuantity } from '@/utils/sumServiceGroupSelectedQuantity'

import { SetupServiceListItem } from './SetupServiceListItem'

export const SetupServiceList = () => {
  const { t } = useTranslation()

  const selectedElement = useAppSelector(mapSelectors.selectedElement)
  const selectedElementQuotableProducts = useAppSelector(
    cartSlice.selectors.selectedElementQuotableProducts
  )
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const selectedElementHasVisibleSeats = useAppSelector(
    mapSelectedElementHasVisibleSeatsSelector
  )
  const selectedElementServices = useAppSelector(mapSelectedElementServices)

  const includedServices = selectedElementServices.includedServices
  const extraServices = selectedElementServices.extraServices
  const ticketServices = selectedElementServices.ticketServices

  if (!selectedElement) return null

  const visibleIncludedServices = getVisibleServices({
    selectedElementHasVisibleSeats,
    services: includedServices,
  })
  const visibleExtraServices = getVisibleServices({
    selectedElementHasVisibleSeats,
    services: extraServices,
  })
  const visibleTicketServices = getVisibleServices({
    selectedElementHasVisibleSeats,
    services: ticketServices,
  })

  const totalGlobalSelectedQuantity = useAppSelector(
    selectTotalGlobalSelectedQuantity
  )
  const totalGlobalBoughtQuantity = useAppSelector(
    totalGlobalBoughtQuantitySelector
  )

  const getOverideValues = (service: Service) => {
    if (service.scheduleType !== 'daily') {
      return {}
    }

    const totalGroupBoughtQuantity = getGroupBoughtQuantity({
      groupId: service.serviceGroupId,
      selectedElementQuotableProducts,
      ticketServices,
    })

    const totalGroupSelectedQuantity = sumServiceGroupSelectedQuantity({
      cartReservations,
      selectedElement,
      selectedElementQuotableProducts,
      service,
      ticketServices,
    })

    // global
    const globalAvailableQnt =
      (service.globalAvailableQnt ?? 0) - totalGlobalBoughtQuantity
    const globalReservedQnt =
      (service.globalReservedQnt ?? 0) + totalGlobalSelectedQuantity

    // group
    const groupAvailableQnt =
      (service.groupAvailableQnt ?? 0) - totalGroupBoughtQuantity
    const groupReservedQnt =
      (service.groupReservedQnt ?? 0) + totalGroupSelectedQuantity

    return {
      globalAvailableQnt,
      globalReservedQnt,
      groupAvailableQnt,
      groupReservedQnt,
    }
  }

  return (
    <div className="flex flex-col">
      {visibleIncludedServices.length > 0 && (
        <div className="flex flex-col gap-3 border-b border-solid py-3">
          <h3 className="font-bold text-primary">
            {t('map.elementDetail.includedServices')}
          </h3>
          {visibleIncludedServices.map((service) => {
            return (
              <SetupServiceListItem key={service.serviceId} service={service} />
            )
          })}
        </div>
      )}

      {visibleTicketServices.length > 0 && (
        <div className="flex flex-col gap-3 border-b border-solid py-3">
          <h3 className="font-bold text-primary">
            {t('map.elementDetail.ticketServices')}
          </h3>
          {visibleTicketServices.map((service) => {
            const overrideValues = getOverideValues(service)

            const listItemService = {
              ...service,
              ...overrideValues,
            }

            return (
              <SetupServiceListItem
                key={service.serviceId}
                service={listItemService}
              />
            )
          })}
        </div>
      )}

      {visibleExtraServices.length > 0 && (
        <div className="flex flex-col gap-3 py-3">
          <h3 className="font-bold text-primary">
            {t('map.elementDetail.extraServices')}
          </h3>
          {visibleExtraServices.map((service) => (
            <SetupServiceListItem key={service.serviceId} service={service} />
          ))}
        </div>
      )}
    </div>
  )
}
